import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = axios.create({
  baseURL: "http://localhost:5000/api",
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

API.interceptors.request.use((req) => {
  const token = localStorage.getItem("token");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

export default function AdminEnterpriseLeads() {
  const [leads, setLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const loadLeads = async () => {
    try {
      setLoading(true);
      setMessage("");
      const res = await API.get("/enterprise-leads");
      setLeads(res?.data?.leads || []);
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await API.patch(`/enterprise-leads/${id}/status`, { status });
      setMessage(res?.data?.message || "Lead updated");
      loadLeads();
    } catch (error) {
      setMessage(error?.response?.data?.message || "Failed to update lead");
    }
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((lead) => {
      const textOk =
        !q ||
        String(lead.companyName || "").toLowerCase().includes(q) ||
        String(lead.contactName || "").toLowerCase().includes(q) ||
        String(lead.email || "").toLowerCase().includes(q) ||
        String(lead.requirements || "").toLowerCase().includes(q);

      const statusOk = statusFilter === "all" || lead.status === statusFilter;
      return textOk && statusOk;
    });
  }, [leads, search, statusFilter]);

  const counts = useMemo(() => ({
    total: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    closed: leads.filter((l) => l.status === "closed").length,
  }), [leads]);

  const styles = {
    page: {
      minHeight: "100vh",
      padding: 28,
      background: "radial-gradient(circle at top left, rgba(59,130,246,0.25), transparent 38%), #020617",
      color: "#f8fafc",
    },
    shell: { maxWidth: 1240, margin: "0 auto" },
    title: { fontSize: 34, fontWeight: 900, margin: "0 0 8px" },
    sub: { color: "#cbd5e1", marginBottom: 22 },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginBottom: 18 },
    stat: { background: "rgba(15,23,42,0.86)", border: "1px solid rgba(148,163,184,0.22)", borderRadius: 20, padding: 18 },
    statLabel: { color: "#94a3b8", fontSize: 12, fontWeight: 900, textTransform: "uppercase" },
    statValue: { fontSize: 32, fontWeight: 900, marginTop: 6 },
    controls: { display: "grid", gridTemplateColumns: "1fr 220px 150px", gap: 12, marginBottom: 16 },
    input: { padding: 13, borderRadius: 14, border: "1px solid #334155", background: "#0f172a", color: "#f8fafc" },
    btn: { border: "none", borderRadius: 14, padding: 13, background: "#22c55e", color: "#052e16", fontWeight: 900, cursor: "pointer" },
    msg: { padding: 14, borderRadius: 14, background: "rgba(37,99,235,0.18)", border: "1px solid rgba(96,165,250,0.35)", marginBottom: 16 },
    list: { display: "grid", gap: 14 },
    card: { background: "rgba(15,23,42,0.9)", border: "1px solid rgba(148,163,184,0.22)", borderRadius: 20, padding: 18 },
    top: { display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
    company: { fontSize: 20, fontWeight: 900 },
    meta: { color: "#94a3b8", marginTop: 4 },
    req: { color: "#e2e8f0", lineHeight: 1.6, marginTop: 14 },
    badge: { padding: "7px 11px", borderRadius: 999, fontWeight: 900, fontSize: 12, background: "#1e293b", border: "1px solid #334155" },
    actions: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 },
    actionBtn: { border: "none", borderRadius: 12, padding: "10px 12px", background: "#2563eb", color: "#fff", fontWeight: 900, cursor: "pointer" },
  };

  const Stat = ({ label, value }) => <div style={styles.stat}><div style={styles.statLabel}>{label}</div><div style={styles.statValue}>{value}</div></div>;

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <h1 style={styles.title}>Enterprise Leads</h1>
        <div style={styles.sub}>Track Personalized / Enterprise plan requests from interested companies.</div>

        <div style={styles.grid}>
          <Stat label="Total Leads" value={counts.total} />
          <Stat label="New" value={counts.new} />
          <Stat label="Contacted" value={counts.contacted} />
          <Stat label="Closed" value={counts.closed} />
        </div>

        <div style={styles.controls}>
          <input style={styles.input} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search company, contact, email, requirement..." />
          <select style={styles.input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="closed">Closed</option>
            <option value="lost">Lost</option>
          </select>
          <button style={styles.btn} onClick={loadLeads}>{loading ? "Loading..." : "Refresh"}</button>
        </div>

        {message ? <div style={styles.msg}>{message}</div> : null}

        <div style={styles.list}>
          {filtered.length === 0 ? (
            <div style={styles.card}>No enterprise leads found.</div>
          ) : filtered.map((lead) => (
            <div key={lead.id} style={styles.card}>
              <div style={styles.top}>
                <div>
                  <div style={styles.company}>{lead.companyName || "Company not provided"}</div>
                  <div style={styles.meta}>{lead.contactName || "-"} • {lead.email || "-"} • {lead.mobileNumber || "-"}</div>
                </div>
                <div style={styles.badge}>{String(lead.status || "new").toUpperCase()}</div>
              </div>

              <div style={styles.req}>{lead.requirements || "No requirements provided."}</div>

              <div style={styles.actions}>
                <button style={styles.actionBtn} onClick={() => updateStatus(lead.id, "contacted")}>Mark Contacted</button>
                <button style={styles.actionBtn} onClick={() => updateStatus(lead.id, "qualified")}>Qualified</button>
                <button style={styles.actionBtn} onClick={() => updateStatus(lead.id, "closed")}>Closed</button>
                <button style={{ ...styles.actionBtn, background: "#dc2626" }} onClick={() => updateStatus(lead.id, "lost")}>Lost</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
